import type { AxiosResponse } from 'axios';
import type {
  CachedResponse,
  CachedWithMeta,
  ExtendedAxiosInstance,
  GetWithCacheOptions,
} from 'types';

import axios from 'axios';
import qs from 'qs';
import { useEffect, useState } from 'react';
import useSWR from 'swr';

import { baseURL } from 'utility/constants';
import { getAuthTokenCookie } from 'utility/helpers';

import { getCacheKey } from './utils';
import { getClientInfo } from './logging';

const apiRequestCache = new Map<string, CachedWithMeta>();

export const clearApiCache = () => apiRequestCache.clear();

const clientApi = axios.create({ baseURL }) as ExtendedAxiosInstance;

clientApi.interceptors.request.use(
  (config) => {
    const token = getAuthTokenCookie();
    if (token) config.headers.Authorization = `Bearer ${token}`;

    // Additional info for logging
    config.headers['X-Client-Info'] = JSON.stringify(getClientInfo());

    if (config.method === 'post') {
      config.transformRequest = (data) => {
        if (data instanceof FormData) {
          return data;
        }
        const formData = new FormData();
        for (const key in data) {
          if (data[key] !== undefined) {
            const value = Array.isArray(data[key]) ? JSON.stringify(data[key]) : data[key];
            formData.append(key, value);
          }
        }

        return formData;
      };
    }

    const contentType = config.headers.get('Content-Type');
    const isJsonContentType =
      typeof contentType === 'string' &&
      contentType.toLowerCase().split(';')[0].trim() === 'application/json';

    if (config.method === 'put' && !isJsonContentType) {
      config.transformRequest = (data) => qs.stringify(data);
    }

    if (config.method === 'patch' && !isJsonContentType) {
      config.transformRequest = (data) => qs.stringify(data);
    }

    if (config.method === 'get') {
      config.paramsSerializer = (params) => qs.stringify(params);
    }

    return config;
  },
  (response) => response,
);

const withJsonHeaders =
  (method: typeof clientApi.post): typeof clientApi.post =>
  (url, data, config = {}) =>
    method(url, data, {
      ...config,
      headers: { 'Content-Type': 'application/json', ...config.headers },
    });

clientApi.postJson = withJsonHeaders(clientApi.post);
clientApi.putJson = withJsonHeaders(clientApi.put);
clientApi.patchJson = withJsonHeaders(clientApi.patch);

const getOptions = (options: any) => {
  const _options = {
    transform: options?.transform || ((d: unknown) => d),
    resetCache: options?.resetCache || false,
    ttlMs: options?.ttlMs || 0,
    ...(options ?? {}),
  };

  return _options;
};

clientApi.getWithCache = async function (
  url: string,
  options: GetWithCacheOptions = {},
): Promise<CachedResponse> {
  const { transform, resetCache, ttlMs, ...config } = getOptions(options);
  const cacheKey = getCacheKey(url, config.params);

  if (!resetCache) {
    const cached: CachedWithMeta | undefined = apiRequestCache.get(cacheKey);

    if (cached) {
      const isStale = ttlMs > 0 && Date.now() - cached.timestamp > ttlMs;

      if (!isStale) {
        // await the data just incase what was stored was an unresolved promise
        const resolvedData = await cached.data;
        return { cache: transform(resolvedData), fromCache: true };
      } else {
        apiRequestCache.delete(cacheKey);
      }
    }
  }

  const resPromise = clientApi.get(url, { ...config });

  const toCache = {
    data: resPromise as unknown as AxiosResponse<any, any>,
    timestamp: Date.now(),
  };

  // cache the result promise so calls prior to resolution can
  // get the cached data instead of making a new api call
  apiRequestCache.set(cacheKey, toCache);

  return resPromise.then((res) => {
    const toCache = {
      data: res,
      timestamp: Date.now(),
    };

    // update the cache with the resolved data
    apiRequestCache.set(cacheKey, toCache);

    return { cache: transform(res), fromCache: false };
  });
};

function useApiResponse(res: any) {
  const [value, setValue] = useState(res);

  useEffect(() => {
    if (JSON.stringify(value) !== JSON.stringify(res)) {
      setValue(res);
    }
  }, [res, value]);

  return value;
}

/**
 * Build key used by SWR for getting data
 * @param {string} route
 * @param {object} [params]
 * @param {number} [params.page]
 * @param {number} [params.pageLimit]
 * @param {string} [params.sortField]
 * @param {'ASC'|'DESC'} [params.sortDirection]
 * @returns
 */
export function buildGetKey(route: string, params = {}) {
  const getKey = (pageIndex: number, previousPageData: any) => {
    // if no route, return null
    if (!route) {
      return null;
    }

    // reached the end
    if (previousPageData && !previousPageData.next) {
      return null;
    }

    const paramsWithPage = {
      ...params,
      page: pageIndex + 1,
    };
    const queryString = qs.stringify(paramsWithPage);

    // first page, we don't have `previousPageData`
    if (pageIndex === 0) return `${route}?${queryString}`;

    // add the cursor to the API endpoint
    return `${route}?${queryString}`;
  };
  return getKey;
}

export function useAPI(url: string, params?: any, config?: any, transform = (data: any) => data) {
  const { data, error, mutate, isValidating } = useSWR(getCacheKey(url, params), async () => {
    const { data } = await clientApi.get(url, {
      ...config,
      params,
    });
    return transform(data);
  });

  const cachedResponse = useApiResponse(data);
  const isLoading = typeof cachedResponse === 'undefined' && !error;

  return {
    data: cachedResponse,
    isLoading,
    error,
    isValidating,
    mutate,
  };
}

export default clientApi;
